import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { formatDate, surface } from "../format";

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography
      sx={{
        fontSize: "0.7rem",
        color: "#6b7280",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.875rem",
        color: "#111827",
        mt: 0.25,
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const UtmBlock = ({ title, data }) => {
  if (!data) {
    return (
      <Paper elevation={0} sx={{ ...surface, p: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1, color: "#111827" }}>
          {title}
        </Typography>
        <Typography sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
          No UTM recorded.
        </Typography>
      </Paper>
    );
  }

  const raw =
    typeof data.raw_utm === "string"
      ? (() => {
          try {
            return JSON.parse(data.raw_utm);
          } catch {
            return null;
          }
        })()
      : data.raw_utm;

  return (
    <Paper elevation={0} sx={{ ...surface, p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography sx={{ fontWeight: 600, color: "#111827" }}>
          {title}
        </Typography>
        {data.plan?.name && (
          <Chip
            size="small"
            label={data.plan.name}
            sx={{ bgcolor: "#f3f4f6" }}
          />
        )}
      </Stack>
      <Grid container spacing={1}>
        <Grid item xs={6} sm={4}>
          <Field label="Source" value={data.utm_source} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="Medium" value={data.utm_medium} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field
            label="Campaign"
            value={data.utm_campaign || data.utm_campaignid}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field
            label="Ad group"
            value={data.adgroup || data.adgroupid || data.adgroup_id}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="Term" value={data.utm_term} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="Device" value={data.device} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="Page type" value={data.page_type} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="Country" value={data.country} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <Field label="City" value={data.city} />
        </Grid>
      </Grid>
      {data.purchase_date && (
        <Typography sx={{ color: "#6b7280", fontSize: "0.75rem", mt: 0.5 }}>
          Purchased {formatDate(data.purchase_date)}
          {data.plan?.credits_granted != null
            ? ` · ${data.plan.credits_granted} credits`
            : ""}
        </Typography>
      )}
      {raw && typeof raw === "object" && Object.keys(raw).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "#6b7280",
              textTransform: "uppercase",
              mb: 0.75,
            }}
          >
            Raw UTM
          </Typography>
          {Object.entries(raw).map(([k, v]) =>
            v ? (
              <Field key={k} label={k.replace(/_/g, " ")} value={String(v)} />
            ) : null,
          )}
        </>
      )}
    </Paper>
  );
};

const OverviewTab = ({ profile }) => {
  const user = profile?.user || {};
  const wallet = profile?.wallet || {};
  const counts = profile?.counts || {};

  return (
    <Stack spacing={2}>
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
        <Box width={{ xs: "100%", md: "60%" }}>
          <Paper elevation={0} sx={{ ...surface, p: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1.5, color: "#111827" }}>
              Profile
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Field label="Username" value={user.username} />
              </Grid>
              <Grid item xs={6}>
                <Field label="Email" value={user.email} />
              </Grid>
              <Grid item xs={6}>
                <Field label="Role" value={user.role} />
              </Grid>
              <Grid item xs={6}>
                <Field label="Organization" value={user.organization} />
              </Grid>
              <Grid item xs={6}>
                <Field label="Contact" value={user.contact} />
              </Grid>
              <Grid item xs={6}>
                <Field
                  label="Location"
                  value={[user.city, user.country].filter(Boolean).join(", ")}
                />
              </Grid>
              <Grid item xs={6}>
                <Field label="Page type" value={user.page_type} />
              </Grid>
              <Grid item xs={6}>
                <Field label="IP" value={user.ip_address} />
              </Grid>
            </Grid>
          </Paper>
        </Box>
        <Box width={{ xs: "100%", md: "40%" }}>
          <Paper elevation={0} sx={{ ...surface, p: 2, height: "100%" }}>
            <Typography sx={{ fontWeight: 600, mb: 1.5, color: "#111827" }}>
              Credits
            </Typography>
            <Stack direction="row" spacing={3} mb={2}>
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {wallet.remaining_credits ?? 0}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Remaining
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {wallet.used_credits ?? 0}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Used
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {wallet.total_credits ?? 0}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                  Lifetime
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Field label="Orders" value={counts.orders} />
            <Field label="Unlocks" value={counts.unlocks} />
            <Field
              label="Credit transactions"
              value={counts.creditTransactions}
            />
          </Paper>
        </Box>
      </Box>

      <UtmBlock title="Signup UTM" data={profile?.signupUtm} />

      {(profile?.purchaseUtms || []).length === 0 ? (
        <UtmBlock title="Credit purchase UTM" data={null} />
      ) : (
        profile.purchaseUtms.map((row) => (
          <UtmBlock key={row.id} title="Credit purchase UTM" data={row} />
        ))
      )}
    </Stack>
  );
};

export default OverviewTab;
